import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import hexRgba from 'hex-rgba';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import CONSTANTS from '../../../app/constants';
import { color } from '../../../app/theme';
import { ConditionsClickType } from '../../../app/types';
import defaultPNG from '../../../assets/images/png/img-default-tamplate-img@3x.png';
import {
  HashtagUrlText,
  WaTempPlaceholderUrlText,
} from '../../shared/HashtagText';
import WhatsAppListCard from '../previews/WhatsAppListCard';

const useStyles = makeStyles(
  () => ({
    root: {},
    text: {
      borderRadius: 12,
      backgroundColor: color.$8,
      fontSize: 13,
      color: color.$13,
      padding: '8px 12px',
      marginBottom: 16,
      maxWidth: '100%',
      width: 'max-content',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      '& .textMessageLink': {
        color: color.$13,
        textDecoration: 'underline',
        '&:hover': {
          opacity: 0.6,
        },
      },
    },
    content: {
      marginBottom: 16,
    },
    audio: {
      borderRadius: 12,
      width: 249,
      height: 148,
    },
    video: {
      borderRadius: 12,
      width: 249,
      height: 148,
    },
    image: {
      width: 249,
      borderRadius: 16,
    },
    imageMap: {
      width: 305,
      borderRadius: 16,
    },
    templateBlock: {
      width: 204,
      marginBottom: 12,
      display: 'flex',
      flexDirection: 'column',
    },
    templateContent: porps => ({
      height: '100%',
      borderRadius: 12,
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      backgroundColor: color.$7,
      display: porps.isCard ? 'flex' : 'block',
      flexDirection: 'column',
    }),
    'templateContent--isImage': {
      position: 'relative',
    },
    templateImage: {
      objectFit: 'cover',
      display: 'block',
      width: 204,
      borderRadius: '12px 12px 0 0',
      margin: 0,
    },
    'templateImage--type-url': {
      width: 204,
      height: 204,
    },
    'templateImage--isImage': {
      borderRadius: 12,
    },
    templateTitle: {
      color: color.$3,
      fontSize: 13,
      padding: '12px 12px 4px 12px',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
    },
    templateSubTitle: porps => ({
      padding: porps.isCard ? '0 12px 12px 12px' : 16,
      color: color.$11,
      fontSize: 13,
      lineHeight: 'normal',
      flex: porps.isCard ? 1 : 'none',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
    }),
    btn: props => ({
      color: color.$2,
      fontSize: 13,
      lineHeight: 1,
      padding: '12px 16px',
      width: props.isCard ? '100%' : '50%',
      display: props.isCard ? 'block' : 'inline-block',
      textAlign: 'center',
      verticalAlign: 'top',
      borderTop: `1px solid ${hexRgba(color.$9, 60)}`,
      wordWrap: 'break-word',
      position: 'relative',
    }),
    templateImageCard: {
      display: 'flex',
      overflowX: 'auto',
      paddingBottom: 8,
      backgroundColor: color.$7,
      width: '100%',
      marginBottom: 16,
      '& > div + div': {
        marginLeft: 8,
      },
    },
    card: {
      display: 'flex',
      overflowX: 'auto',
      paddingBottom: 8,
      backgroundColor: color.$7,
      width: '100%',
      marginBottom: 16,
      '& > div + div': {
        marginLeft: 8,
      },
    },
    confirm: {
      marginBottom: 16,
    },
    action: {
      position: 'absolute',
      border: 'solid 1px #2ca6e0',
      backgroundColor: 'rgba(44, 166, 224, 0.4)',
    },
    tempBtnIndex: {
      position: 'absolute',
      minWidth: 20,
      height: 20,
      color: '#fff',
      backgroundColor: '#000',
      textAlign: 'center',
      borderRadius: '11px',
      padding: '4px 8px',
      fontSize: 13,
      fontWeight: 500,
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 1,
      letterSpacing: 'normal',
    },
    confirmAndCardTempBtnIndex: {
      top: 'calc(50% - 10px)',
      left: '4px',
    },
    confirmButtonTitle: {
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: 14,
    },
    imageTempBtnIndex: {
      top: '4px',
      left: '4px',
    },
    imageMapTempBtnIndex: {
      top: '0px',
      left: '0px',
    },
    richvideo: {
      width: 414,
      borderRadius: 12,
    },
    playerIconBlock: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'max-content',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 5,
    },
    playerIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '6px 8px',
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$7,
      '& > img': {
        marginRight: 9,
      },
    },
    playerIndex: {
      width: '100%',
      height: '48px',
      position: 'absolute',
      left: 0,
      zIndex: 6,
      '& > a': {
        width: '100%',
        height: '100%',
        padding: '4px',
      },
    },
    playerCover: {
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      position: 'absolute',
      borderRadius: '12px',
      zIndex: 4,
    },
  }),
  {
    name: 'MessageContent',
  },
);

function Template({
  type,
  imageType,
  imageUrl,
  subTitle,
  title,
  buttons,
  elementTempBtnIndexTotal,
  isHideIndex,
}) {
  const isCard = type === 'card';
  const isImage = type === 'image';
  const classes = useStyles({ isCard });
  const [bgSrc, setBgSrc] = useState(defaultPNG);
  const tempBtnIndex = elementTempBtnIndexTotal - buttons.length;

  useEffect(() => {
    if (imageUrl) {
      const bgImg = new Image();
      bgImg.onload = () => {
        setBgSrc(bgImg.src);
      };
      bgImg.src = imageUrl;
    }
  }, []);

  const btnContent = () =>
    (buttons || []).map((btn, i) => {
      const { title: btnTitle } = btn;
      return (
        <div key={`${btnTitle}-${uuidv4()}`} className={classes.btn}>
          {!isHideIndex && (
            <div
              className={classnames(
                classes.confirmAndCardTempBtnIndex,
                classes.tempBtnIndex,
              )}
            >
              {tempBtnIndex + i}
            </div>
          )}
          <span className={classes.confirmButtonTitle}>{btnTitle}</span>
        </div>
      );
    });

  return (
    <div className={classes.templateBlock}>
      <div
        className={classnames(classes.templateContent, {
          [classes['templateContent--isImage']]: isImage,
        })}
      >
        {(isCard || isImage) && (
          <>
            {isImage && !isHideIndex && (
              <div
                className={classnames(
                  classes.imageTempBtnIndex,
                  classes.tempBtnIndex,
                )}
              >
                {tempBtnIndex}
              </div>
            )}
            <img
              className={classnames(classes.templateImage, {
                [classes['templateImage--type-url']]: imageType === 'url',
                [classes['templateImage--isImage']]: isImage,
              })}
              src={bgSrc}
              alt={title}
            />
            {isCard && <div className={classes.templateTitle}>{title}</div>}
          </>
        )}
        {!isImage && (
          <>
            <div className={classes.templateSubTitle}>{subTitle}</div>
            {btnContent()}
          </>
        )}
      </div>
    </div>
  );
}

const MessageContent = () => {
  const classes = useStyles();
  const { data, selectedNode } = useSelector(state => state.ma);
  const { nodes = [] } = data || {};
  const { source, clickType } = selectedNode?.data;
  const sourceNode = nodes.find(n => n?.id === source);
  const { messages } = sourceNode?.data || [];
  const isAny = clickType === ConditionsClickType.ANY;

  let elementTempBtnIndex = 1;

  const renderActionBlock = (
    msgData,
    elementTempBtnIndexTotal,
    isHideIndex,
  ) => {
    const screenSize = 305;
    const { size, buttons } = msgData || {};
    const maxHeight = size?.height * (screenSize / size?.width);
    const tempBtnIndex = elementTempBtnIndexTotal - buttons.length;
    return buttons.map((button, index) => {
      const { height, width, x, y, title, type } = button;
      if (type === 'url') {
        return (
          <a
            key={`ImageMapActionBlock-${index}`}
            className={classes.action}
            href={title}
            rel="noopener noreferrer"
            aria-label="message link"
            style={{
              height: parseFloat(height) * 0.01 * maxHeight,
              width: parseFloat(width) * 0.01 * screenSize,
              top: parseFloat(y) * 0.01 * maxHeight,
              left: parseFloat(x) * 0.01 * screenSize,
            }}
            target="_blank"
          >
            {!isHideIndex && (
              <div
                className={classnames(
                  classes.imageMapTempBtnIndex,
                  classes.tempBtnIndex,
                )}
              >
                {tempBtnIndex + index}
              </div>
            )}
            <i className="fa fa-phone" aria-hidden="true" />
          </a>
        );
      }
      return (
        <div
          key={`ImageMapActionBlock-${index}`}
          className={classes.action}
          style={{
            height: parseFloat(height) * 0.01 * maxHeight,
            width: parseFloat(width) * 0.01 * screenSize,
            top: parseFloat(y) * 0.01 * maxHeight,
            left: parseFloat(x) * 0.01 * screenSize,
          }}
        >
          {!isHideIndex && (
            <div
              className={classnames(
                classes.imageMapTempBtnIndex,
                classes.tempBtnIndex,
              )}
            >
              {tempBtnIndex + index}
            </div>
          )}
        </div>
      );
    });
  };

  const messagesComponents = (messages || []).map(msg => {
    const { contentType, data: msgData, index } = msg;

    switch (contentType) {
      case CONSTANTS.MESSAGE_TYPE.TEXT_PLAIN_TYPE:
        const isWhatsappTemplate = !!msg?.waTemplateId;
        return (
          msgData?.content && (
            <div
              key={`template-x-text-${index}`}
              className={classes.text}
              dangerouslySetInnerHTML={{
                __html:
                  isWhatsappTemplate &&
                  msg?.data?.defaultParameters &&
                  msg?.data?.defaultParameters?.length
                    ? WaTempPlaceholderUrlText(msg?.data?.content)
                    : HashtagUrlText(msg?.data?.content),
              }}
            />
          )
        );
      case CONSTANTS.MESSAGE_TYPE.AUDIO_TYPE:
        return (
          msgData?.url && (
            <div key={`template-x-audio-${index}`} className={classes.content}>
              <audio className={classes.audio} src={msgData?.url} controls>
                <track kind="captions" />
              </audio>
            </div>
          )
        );
      case CONSTANTS.MESSAGE_TYPE.IMAGE_TYPE:
        return (
          msgData?.url && (
            <div key={`template-x-image-${index}`} className={classes.content}>
              <img
                className={classes.image}
                src={msgData?.url}
                alt={`${msgData?.name}`}
              />
            </div>
          )
        );
      case CONSTANTS.MESSAGE_TYPE.VIDEO_TYPE:
        return (
          msgData?.url && (
            <div key={`template-x-video-${index}`} className={classes.content}>
              <video className={classes.video} src={msgData?.url} controls>
                <track kind="captions" />
              </video>
            </div>
          )
        );
      case CONSTANTS.MESSAGE_TYPE.IMAGE_MAP_TYPE:
        return (
          msgData?.baseUrl && (
            <div
              key={`template-x-imagemap-${index}`}
              className={classes.content}
              style={{
                position: 'relative',
              }}
            >
              <img
                className={classes.imageMap}
                src={msgData?.baseUrl}
                alt={`${msgData?.altText}`}
              />
              {renderActionBlock(msgData)}
            </div>
          )
        );
      case CONSTANTS.MESSAGE_TYPE.TEMPLATE_TYPE:
        const { templateType } = msgData;
        let { elements } = msgData;
        if (templateType === 'button') {
          elements = [
            {
              buttons: msgData.buttons,
              title: msgData.text,
            },
          ];
        }
        switch (templateType) {
          case 'button':
          case 'confirm':
            return (
              <div
                key={`template-x-confirm-card-${index}`}
                className={classes.confirm}
              >
                {elements.map((elm, i) => {
                  const { buttons, title } = elm;
                  elementTempBtnIndex += buttons.length;
                  return (
                    <Template
                      key={`template-confirm-${i}`}
                      type={templateType}
                      subTitle={title}
                      buttons={buttons}
                      index={i}
                      elementTempBtnIndexTotal={elementTempBtnIndex}
                      isHideIndex={isAny}
                    />
                  );
                })}
              </div>
            );
          case 'card':
            return (
              <div
                key={`template-x-optional-card-${index}`}
                className={classes.card}
              >
                {elements.map((elm, i) => {
                  const { buttons, title, subtitle, imageType, imageUrl } = elm;
                  elementTempBtnIndex += buttons.length;
                  return (
                    <Template
                      key={`template-card-${i}`}
                      type={templateType}
                      imageType={imageType}
                      imageUrl={imageUrl}
                      title={title}
                      subTitle={subtitle}
                      buttons={buttons}
                      index={i}
                      elementTempBtnIndexTotal={elementTempBtnIndex}
                      isHideIndex={isAny}
                    />
                  );
                })}
              </div>
            );
          case 'image':
            return (
              <div
                key={`template-x-image-card-${index}`}
                className={classes.templateImageCard}
              >
                {elements.map((elm, i) => {
                  const { imageType, imageUrl, buttons } = elm;
                  elementTempBtnIndex += buttons.length;
                  return (
                    <Template
                      key={`template-image-${i}`}
                      type={templateType}
                      imageType={imageType}
                      imageUrl={imageUrl}
                      buttons={buttons}
                      index={i}
                      elementTempBtnIndexTotal={elementTempBtnIndex}
                      isHideIndex={isAny}
                    />
                  );
                })}
              </div>
            );
          case 'imagemap':
            return elements.map(elm => {
              const { buttons } = elm;
              elementTempBtnIndex += buttons.length;
              return (
                <div
                  key={`template-x-imagemap-${index}`}
                  className={classes.content}
                  style={{
                    position: 'relative',
                  }}
                >
                  <img
                    className={classes.imageMap}
                    src={elm.imageUrl}
                    alt={elm.title}
                  />
                  {renderActionBlock(elm, elementTempBtnIndex, isAny)}
                </div>
              );
            });
          case 'waList':
            return (
              <div style={{ marginBottom: 16 }}>
                {elements.map((elm, i) => {
                  elementTempBtnIndex += elm?.buttons?.length;
                  return (
                    <WhatsAppListCard
                      key={`default-template-preview-wa-list-card-${i}`}
                      data={elm}
                      messageIndex={0}
                      index={i}
                      elementTempBtnIndexTotal={elementTempBtnIndex}
                      isShowList
                      isShowElementTempBtnIndex={!isAny}
                    />
                  );
                })}
              </div>
            );
          default:
            return null;
        }
      default:
        return null;
    }
  });

  return <div className={classes.root}>{messagesComponents}</div>;
};

export default MessageContent;
